import { For } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Readout } from '~/components/primitives/readout';
import { SegBar } from '~/components/primitives/seg-bar';
import { Glyph } from '~/components/glyph';
import { factions, skills } from '~/mock/profile';
import { player } from '~/lib/player';
import type { Tone } from '~/types/game';

function sideTone(side: 'ally' | 'enemy' | 'neutral'): Tone {
  if (side === 'ally') return 'action';
  if (side === 'enemy') return 'hostile';
  return 'data';
}

/** OP screen — operator profile, stats, reputation, skill progression. */
export function Op() {
  return (
    <>
      <div
        style={{
          padding: '8px 12px',
          'border-bottom': '1px solid var(--line-faint)',
          display: 'flex',
          'align-items': 'center',
          gap: '8px',
        }}
      >
        <Glyph name="profile" size={16} color="var(--data)" />
        <span class="t-mono" style={{ 'font-size': '14px', color: 'var(--fg)' }}>OPERATOR</span>
      </div>

      <div style={{ padding: '12px' }}>
        <div class="panel-elev" style={{ padding: '12px', display: 'flex', gap: '10px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              background: 'var(--sf-input)',
              border: '1px solid var(--line-strong)',
              position: 'relative',
              'flex-shrink': 0,
            }}
          >
            <svg viewBox="0 0 64 64" width="64" height="64">
              <rect x="0" y="0" width="64" height="64" fill="none" />
              <line x1="0" y1="0" x2="64" y2="64" stroke="var(--line-strong)" stroke-width="0.5" />
              <line x1="64" y1="0" x2="0" y2="64" stroke="var(--line-strong)" stroke-width="0.5" />
              <circle cx="32" cy="32" r="14" fill="none" stroke="var(--data)" stroke-width="1.2" />
              <text
                x="32"
                y="36"
                text-anchor="middle"
                fill="var(--data)"
                font-family="var(--mono)"
                font-size="10"
                letter-spacing="0.1em"
              >
                W//00
              </text>
            </svg>
          </div>
          <div style={{ flex: 1, display: 'flex', 'flex-direction': 'column', gap: '4px', 'min-width': 0 }}>
            <div class="t-mono" style={{ 'font-size': '16px', color: 'var(--fg)', 'letter-spacing': '0.04em' }}>
              WRAITH//00
            </div>
            <div class="t-micro">REGISTERED · 2034-08-12 · SAGINAW K-7</div>
            <div style={{ display: 'flex', gap: '6px', 'margin-top': '4px' }}>
              <Badge tone="data">TIER 3</Badge>
              <Badge tone="action">CLEAN</Badge>
              <Badge tone="warn">FLAGGED ×2</Badge>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '0 12px 12px',
          display: 'grid',
          'grid-template-columns': 'repeat(3,1fr)',
          gap: '6px',
        }}
      >
        <div class="panel" style={{ padding: '8px' }}>
          <Readout v={`¤${player.credits().toLocaleString()}`} l="CRED" tone="action" />
        </div>
        <div class="panel" style={{ padding: '8px' }}>
          <Readout v="072" l="REP" tone="data" />
        </div>
        <div class="panel" style={{ padding: '8px' }}>
          <Readout v="041" l="JOBS" />
        </div>
      </div>

      <div style={{ padding: '0 12px 6px' }}>
        <div class="t-label">SKILLS</div>
      </div>
      <div style={{ padding: '0 12px', display: 'flex', 'flex-direction': 'column', gap: '8px' }}>
        <For each={skills}>
          {(s) => (
            <div>
              <div style={{ display: 'flex', 'align-items': 'baseline', 'margin-bottom': '4px' }}>
                <span class="t-mono" style={{ 'font-size': '11px', color: 'var(--fg)' }}>{s.name}</span>
                <span style={{ flex: 1 }} />
                <span class="t-mono t-tab" style={{ 'font-size': '11px', color: `var(--${s.tone})` }}>
                  {s.lv}
                  <span style={{ color: 'var(--fg-dim)' }}> / {s.max}</span>
                </span>
              </div>
              <SegBar on={s.lv} total={s.max} tone={s.tone} />
            </div>
          )}
        </For>
      </div>

      <div style={{ padding: '12px 12px 6px' }}>
        <div class="t-label">FACTIONS</div>
      </div>
      <div style={{ padding: '0 12px 12px', display: 'flex', 'flex-direction': 'column', gap: '6px' }}>
        <For each={factions}>
          {(f) => {
            const tone = sideTone(f.side);
            return (
              <div class="panel" style={{ padding: '8px', display: 'flex', 'align-items': 'center', gap: '8px' }}>
                <span style={{ width: '6px', height: '6px', background: `var(--${tone})` }} />
                <span class="t-mono" style={{ 'font-size': '11px', color: 'var(--fg)', flex: 1 }}>
                  {f.name}
                </span>
                <div class="bar" style={{ width: '90px' }}>
                  <i style={{ width: `${f.rel}%`, background: `var(--${tone})` }} />
                </div>
                <span
                  class="t-mono t-tab"
                  style={{
                    'font-size': '10px',
                    color: `var(--${tone})`,
                    width: '26px',
                    'text-align': 'right',
                  }}
                >
                  {f.rel}
                </span>
              </div>
            );
          }}
        </For>
      </div>
    </>
  );
}
